import { useState } from 'react';
import { line, t } from '@bms2/shared';
import { AlertBanner, Checkbox } from '../../components/controls';
import { Money } from '../../components/Money';
import { Modal } from '../../components/modals/Modal';
import { INVOICES_PATH } from './api';
import styles from './RaiseInvoiceWindow.module.css';

/** The shipping charges an order has not billed yet, as Raise an Invoice reads them (T1132; decision 2531). */
export interface UnbilledShippingCharge {
  shipmentId: string;
  shipmentNumber: string;
  carrier: string | null;
  shippedDate: string | null;
  chargeCents: number;
}

/** 🔒 Registered with the path guard: the charges an order offers and the raise that takes the ticked ones (decision 2531). */
export const invoiceShippingChargesPath = (orderId: string): string => `${INVOICES_PATH}/shipping-charges/${orderId}`;
export const invoiceFromOrderPath = (orderId: string): string => `${INVOICES_PATH}/from-sales-order/${orderId}`;

/**
 * 🔒 Raise an Invoice, where the order has shipped charges nobody has billed
 * (T1132; decision 2531): each is ticked by default, because shipping is
 * prepay-and-add and the common answer is to bill it. An unticked charge stays
 * for a later invoice.
 */
export function RaiseInvoiceWindow({ charges, busy, error, onRaise, onCancel, onDismissError }: {
  charges: readonly UnbilledShippingCharge[];
  busy: boolean;
  error: string | null;
  onRaise: (shipmentIds: string[]) => void;
  onCancel: () => void;
  onDismissError: () => void;
}) {
  const [ticked, setTicked] = useState<Record<string, boolean>>(() => Object.fromEntries(charges.map((c) => [c.shipmentId, true])));
  return (
    <Modal
      open
      title={t('salesOrder.invoice.title')}
      onCancel={onCancel}
      dataModal="sales-order-raise-invoice"
      actions={[
        { kind: 'primary', label: t('salesOrder.invoice.raise'), onAct: () => onRaise(charges.filter((c) => ticked[c.shipmentId]).map((c) => c.shipmentId)), disabled: busy },
        { kind: 'cancel', label: t('modal.cancel'), onAct: onCancel },
      ]}
    >
      {error ? <AlertBanner tone="error" message={error} action={{ label: t('modal.dismiss'), onAction: onDismissError }} /> : null}
      <h3>{t('salesOrder.invoice.charges')}</h3>
      <p className={styles.muted}>{t('salesOrder.invoice.chargesHint')}</p>
      <ul className={styles.list} data-invoice-shipping-charges={charges.length}>
        {charges.map((charge) => (
          <li key={charge.shipmentId} className={styles.row} data-invoice-shipping-charge={charge.shipmentId}>
            <Checkbox label={line(charge.shipmentNumber, charge.carrier ? `· ${charge.carrier}` : '', charge.shippedDate ? `· ${charge.shippedDate}` : '')} checked={ticked[charge.shipmentId] === true} onChange={(on) => setTicked((was) => ({ ...was, [charge.shipmentId]: on }))} />
            <Money cents={charge.chargeCents} />
          </li>
        ))}
      </ul>
    </Modal>
  );
}
